import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { FaDownload } from "react-icons/fa";
import sampleGuideImage from "../assets/4.jpg";
import "../style/home.css";

function Home() {
  const navigate = useNavigate();

  const [wings, setWings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [flats, setFlats] = useState([]);

  const [selectedWing, setSelectedWing] = useState("");
  const [selectedFloor, setSelectedFloor] = useState("");
  const [selectedFlat, setSelectedFlat] = useState("");
  const [previousReading, setPreviousReading] = useState("");
  const [currentReading, setCurrentReading] = useState("");
  const [difference, setDifference] = useState("");
  const [uploaderName, setUploaderName] = useState("");
  const [uploaderContact, setUploaderContact] = useState("");
  const [billFile, setBillFile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isHintOpen, setIsHintOpen] = useState(false);

  const fileInputRef = useRef(null);

  // Fetch Wings
  useEffect(() => {
    const fetchWings = async () => {
      try {
        const response = await api.get("/wings");
        setWings(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching wings:", error);
        setError("Failed to load wings");
        setLoading(false);
      }
    };
    fetchWings();
  }, []);

  // Fetch Floors based on selected Wing
  useEffect(() => {
    if (!selectedWing) {
      setFloors([]);
      setSelectedFloor("");
      setFlats([]);
      setSelectedFlat("");
      return;
    }

    const fetchFloors = async () => {
      try {
        const response = await api.get(`/floors/wing/${selectedWing}`);
        setFloors(response.data);
      } catch (error) {
        console.error("Error fetching floors:", error);
        setFloors([]);
      }
    };

    fetchFloors();
  }, [selectedWing]);

  // Fetch Flats based on selected Floor
  useEffect(() => {
    if (!selectedFloor) {
      setFlats([]);
      setSelectedFlat("");
      return;
    }

    const fetchFlats = async () => {
      try {
        const response = await api.get(`/flats/floor/${selectedFloor}`);
        setFlats(response.data);
      } catch (error) {
        console.error("Error fetching flats:", error);
        setFlats([]);
      }
    };

    fetchFlats();
  }, [selectedFloor]);

  // Fetch Previous Reading based on selected Flat
  useEffect(() => {
    if (!selectedFlat) {
      setPreviousReading("");
      return;
    }

    const fetchPreviousReading = async () => {
      try {
        const response = await api.get(`/meter-readings/${selectedFlat}/previous`);
        setPreviousReading(parseFloat(response.data.toString()) || 0.0);
      } catch (error) {
        console.error("Error fetching previous reading:", error);
        setPreviousReading(0.0);
      }
    };

    fetchPreviousReading();
  }, [selectedFlat]);

  const handleWingChange = (e) => {
    setSelectedWing(e.target.value);
    setSelectedFloor("");
    setSelectedFlat("");
  };

  const handleFloorChange = (e) => {
    setSelectedFloor(e.target.value);
    setSelectedFlat("");
  };

  const handleFlatChange = (e) => {
    setSelectedFlat(e.target.value);
  };

  const handleProceed = () => {
    if (!selectedWing) {
      alert("Please select Building");
      return;
    }
    if (!selectedFloor) {
      alert("Please select Floor");
      return;
    }
    if (!selectedFlat) {
      alert("Please select Flat No.");
      return;
    }

    setShowForm(true);
  };

  const handleCurrentReadingChange = (e) => {
    const value = e.target.value;
    setCurrentReading(value);
    const prev = typeof previousReading === "number" ? previousReading : parseFloat(previousReading || 0);
    setDifference(value ? (parseFloat(value) - prev).toFixed(2) : "");
  };

  const handleUploaderContactChange = (e) => {
    const value = e.target.value;
    if (/^\d{0,10}$/.test(value)) {
      setUploaderContact(value);
    }
  };

  const handleUploaderNameChange = (e) => {
    const value = e.target.value;
    const filteredValue = value.replace(/[^A-Za-z ]/g, "");
    setUploaderName(filteredValue);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type;
      const fileSizeMB = file.size / (1024 * 1024);
      if ((fileType === "image/jpeg" || fileType === "image/png" || fileType === "image/jpg") && fileSizeMB <= 6) {
        setBillFile(file);
      } else {
        alert("Please upload a JPG or PNG file under 6MB.");
        e.target.value = "";
      }
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const selectedWingObj = wings.find((wing) => wing.id == selectedWing);
  const selectedFlatObj = flats.find((flat) => flat.id == selectedFlat);

  const handleSubmit = async () => {
    if (!currentReading) {
      alert("Please enter current reading");
      return;
    }
    if (!uploaderName.trim()) {
      alert("Please enter uploader name");
      return;
    }
    if (uploaderContact.length !== 10) {
      alert("Please enter uploader contact number (10 digits)");
      return;
    }
    if (!billFile) {
      alert("Please upload meter image file");
      return;
    }

    const formData = new FormData();
    formData.append("wingId", selectedWing);
    formData.append("flatId", selectedFlat);
    formData.append("previousReading", previousReading || 0);
    formData.append("currentReading", currentReading);
    formData.append("uploaderName", uploaderName);
    formData.append("uploaderContact", uploaderContact);
    formData.append("file", billFile);

    setSubmitting(true);
    try {
      const response = await api.post("/meter-readings/add", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200 || response.status === 201) {
        alert("File uploaded successfully!");
        navigate("/confirmation", {
          state: {
            wing: selectedWingObj ? selectedWingObj.name : "",
            flat: (selectedWingObj ? selectedWingObj.name : "") + " - " + (selectedFlatObj ? selectedFlatObj.flatNo : ""),
            mobileNumber: uploaderContact,
            name: uploaderName,
            previousReading: previousReading || 0,
            currentReading,
            waterUsage: difference,
            billFileName: billFile.name,
          },
        });
      } else {
        alert("Failed to upload file. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("An error occurred while submitting. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // PWA Install
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          console.log("User accepted PWA install");
        }
        setDeferredPrompt(null);
        setIsInstallable(false);
      });
    }
  };

  const [currentDate, setCurrentDate] = useState("");

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).replace(",", "");
    setCurrentDate(formattedDate);
  }, []);

  return (
    <div className="home-container">
      <nav className="navbars">
        <div className="logo">
          {isInstallable && (
            <button
              id="install-button"
              onClick={handleInstall}
              className="login-btn"
              style={{
                padding: "8px 16px",
                background: "green",
                color: "#fff",
                border: "none",
                borderRadius: "20px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <FaDownload /> Install PWA
            </button>
          )}
        </div>
      </nav>

      <div className="contents">
        <div className="text-content">
          <h2 className="special">Upload Water Meter Reading</h2>
          <div className="form-group" style={{ marginBottom: "15px" }}>
            <span className="form-labels" style={{ fontSize: "18px" }}>
              Today's Date : {currentDate}
            </span>
          </div>

          {/* Building Selection */}
          {!showForm && (
            <div className="form-group">
              <label className="form-label">Building:</label>
              {loading ? (
                <p>Loading Buildings...</p>
              ) : error ? (
                <p style={{ color: "red" }}>{error}</p>
              ) : (
                <select className="dropdown" value={selectedWing} onChange={handleWingChange}>
                  <option value="">Select Building</option>
                  {wings.map((wing) => (
                    <option key={wing.id} value={wing.id}>
                      {wing.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Floor Selection */}
          {!showForm && (
            <div className="form-group">
              <label className="form-label">Floor:</label>
              <select className="dropdown custom-dropdown" value={selectedFloor} onChange={handleFloorChange}>
                <option value="">Select Floor</option>
                {floors.map((floor) => (
                  <option key={floor.id} value={floor.id}>
                    {floor.floorNo}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Flat Selection */}
          {!showForm && (
            <div className="form-group">
              <label className="form-label">Flat No.:</label>
              <select className="dropdown" value={selectedFlat} onChange={handleFlatChange}>
                <option value="">Select Flat</option>
                {flats.map((flat) => (
                  <option key={flat.id} value={flat.id}>
                    {(selectedWingObj ? selectedWingObj.name : "") + " - " + flat.flatNo}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Proceed Button */}
          {!showForm && (
            <button className="fetch-btn" onClick={handleProceed} style={{ marginTop: "15px" }}>
              Proceed
            </button>
          )}

          {/* Form after Proceed */}
          {showForm && (
            <>
              <div className="form-group">
                <label className="form-label">Flat No.:</label>
                <input 
                  type="text" 
                  className="input-box" 
                  value={(selectedWingObj ? selectedWingObj.name : "") + " - " + (selectedFlatObj ? selectedFlatObj.flatNo : "")} 
                  readOnly 
                  style={{ backgroundColor: "#f8f8f8", color: "#666" }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Enter Current Reading:</label>
                <input
                  type="number"
                  step="0.01"
                  className="input-box"
                  placeholder="Enter reading (5 digits)"
                  value={currentReading}
                  onChange={handleCurrentReadingChange}
                />
              </div>

              <div className="hint" style={{ textAlignment: "right", margin: "-6px 0 12px 0" }}>
                <a 
                  href="#help" 
                  onClick={(e) => { e.preventDefault(); setIsHintOpen(true); }}
                  style={{ color: "#007bff", textDecoration: "underline", fontSize: "14px", fontWeight: "bold" }}
                >
                  Click For Help
                </a>
              </div>

              <div className="form-group" style={{ flexDirection: "column", alignItems: "stretch" }}>
                <button 
                  type="button" 
                  className="upload-button" 
                  onClick={handleButtonClick}
                  style={{ 
                    width: "100%", 
                    padding: "12px", 
                    border: "1.5px solid #007bff", 
                    backgroundColor: "#ffffff", 
                    color: "#007bff", 
                    fontWeight: "bold", 
                    borderRadius: "5px", 
                    cursor: "pointer", 
                    fontSize: "16px",
                    textAlign: "center"
                  }}
                >
                  {billFile ? billFile.name : "Upload Meter Image"}
                </button>
                <span style={{ fontSize: "12px", color: "#666", textAlign: "center", marginTop: "4px" }}>
                  Max file size allowed: 6MB*
                </span>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="file-input"
                  accept="image/jpeg, image/png, image/jpg"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Uploader Name:</label>
                <input
                  type="text"
                  className="input-box"
                  value={uploaderName}
                  onChange={handleUploaderNameChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Uploader Contact Number:</label>
                <input
                  type="text"
                  className="input-box"
                  value={uploaderContact}
                  onChange={handleUploaderContactChange}
                />
              </div>

              <div className="confirmation-boxxx" style={{ backgroundColor: "#f8faf8", border: "1px solid #e2eee2", padding: "12px", borderRadius: "8px", margin: "16px 0", textAlign: "left" }}>
                <p style={{ color: "#28a745", fontWeight: "bold", margin: 0, fontSize: "14px" }}>
                  Note: Avoid average billing by submitting meter reading
                </p>
              </div>

              <div style={{ marginTop: "15px" }}>
                <button 
                  type="button" 
                  className="confirm-btns" 
                  disabled={submitting} 
                  onClick={handleSubmit}
                  style={{ backgroundColor: "#ff6b6b", color: "#ffffff", border: "none", padding: "10px 24px", fontSize: "16px", borderRadius: "5px", cursor: "pointer", margin: "0" }}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Help Modal */}
      {isHintOpen && (
        <div className="modal-overlay" onClick={() => setIsHintOpen(false)}>
          <div className="modal-contents" onClick={(e) => e.stopPropagation()}>
            <span className="close" onClick={() => setIsHintOpen(false)}>&times;</span>
            <h3 style={{ textAlign: "center", marginBottom: "15px", color: "#5a4432" }}>Meter Reading Help Guide</h3>
            <img src={sampleGuideImage} alt="Meter Reading Guide" style={{ width: "100%", borderRadius: "6px" }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;